import { useState } from 'react'
import { X, Search, UserCheck, UserPlus, RefreshCw } from 'lucide-react'
import { usePlayers, useCreatePlayer, useGhinLookup } from '../hooks/usePlayers'
import { useAuth } from '../contexts/AuthContext'
import { getInitials } from '../utils/colors'
import { hasHandicapIndex, formatHandicapIndex, combineHandicapInput, splitHandicapForInput } from '../utils/handicaps'
import PlusHandicapToggle from './shared/PlusHandicapToggle'
import './Modal.css'

const AVATAR_COLORS = [
  '#0ea47a', '#5e9be6', '#f59e0b', '#ef4444',
  '#a78bfa', '#f472b6', '#fb923c', '#22d3ee',
]

export default function AddPlayerModal({ onClose, startManual = false }) {
  const { user } = useAuth()
  const { data: allPlayers } = usePlayers()
  const [searchText, setSearchText] = useState('')
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [showManual, setShowManual] = useState(startManual)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [color, setColor] = useState(AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)])
  const [ghinNumber, setGhinNumber] = useState('')
  const [handicapIndex, setHandicapIndex] = useState('')
  const [isPlus, setIsPlus] = useState(false)
  const [gender, setGender] = useState('M')
  const createPlayer = useCreatePlayer()
  const ghinLookup = useGhinLookup()

  // Search through local players (registered users + user's manual players)
  const searchResults = searchText.length >= 1 && allPlayers
    ? allPlayers.filter((p) => {
        // Show registered users and user's own manual players
        if (!p.user_id && p.created_by !== user?.id) return false
        const q = searchText.toLowerCase()
        return p.first_name?.toLowerCase().startsWith(q) ||
               p.last_name?.toLowerCase().startsWith(q) ||
               p.email?.toLowerCase().startsWith(q) ||
               p.display_name?.toLowerCase().split(/\s+/).some(w => w.startsWith(q))
      })
    : []

  const handleSelectPlayer = (player) => {
    setSelectedPlayer(player)
    setName(player.first_name || player.last_name
      ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
      : player.display_name)
    setEmail(player.email || '')
    setShowManual(false)
  }

  const handleClearSelection = () => {
    setSelectedPlayer(null)
    setName('')
    setEmail('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return

    // If an existing player was selected, they already have a record — just close
    if (selectedPlayer) {
      onClose()
      return
    }

    const handicapValue = combineHandicapInput({ isPlus, magnitude: handicapIndex })

    createPlayer.mutate(
      {
        display_name: name.trim(),
        email: email.trim(),
        avatar_color: color,
        ...(ghinNumber.trim() ? { ghin_number: ghinNumber.trim() } : {}),
        ...(handicapValue != null ? { handicap_index: handicapValue } : {}),
        gender,
      },
      { onSuccess: () => onClose() }
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Player</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-form">
            {/* Search for existing players */}
            {!selectedPlayer && !showManual && (
              <>
                <div className="form-group">
                  <label>Find a player</label>
                  <div style={{ position: 'relative' }}>
                    <Search
                      size={18}
                      style={{
                        position: 'absolute',
                        left: '0.875rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)',
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Search by name or email..."
                      autoFocus
                      style={{ paddingLeft: '2.75rem' }}
                    />
                  </div>
                </div>

                {/* Search results */}
                {searchText.length >= 1 && (
                  <div style={{ marginBottom: '1rem' }}>
                    {searchResults.length === 0 && (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', padding: '0.5rem 0' }}>
                        No players found
                      </p>
                    )}
                    {searchResults.map((player) => (
                      <button
                        key={player.id}
                        type="button"
                        onClick={() => handleSelectPlayer(player)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.875rem',
                          marginBottom: '0.5rem',
                          background: 'var(--bg-tertiary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '9999px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          color: 'var(--text-primary)',
                          transition: 'border-color 0.15s',
                        }}
                      >
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: player.avatar_color || AVATAR_COLORS[searchResults.indexOf(player) % AVATAR_COLORS.length],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '0.6875rem',
                          color: '#fff',
                          flexShrink: 0,
                        }}>
                          {getInitials(player)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                            {player.first_name || player.last_name
                              ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
                              : player.display_name}
                          </div>
                          {player.display_name && (player.first_name || player.last_name) && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              {player.display_name}
                            </div>
                          )}
                          {hasHandicapIndex(player.handicap_index) && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              HCP: {formatHandicapIndex(player.handicap_index)}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Divider + manual add option */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  margin: '0.5rem 0 1rem',
                }}>
                  <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>or</span>
                  <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
                </div>

                <button
                  type="button"
                  onClick={() => setShowManual(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    padding: '0.875rem',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '9999px',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    transition: 'border-color 0.15s',
                  }}
                >
                  <UserPlus size={18} />
                  Add manually
                </button>
              </>
            )}

            {/* Selected player confirmation */}
            {selectedPlayer && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.875rem',
                marginBottom: '1rem',
                background: 'rgba(14, 164, 122, 0.1)',
                border: '1px solid rgba(14, 164, 122, 0.3)',
                borderRadius: '0.5rem',
              }}>
                <UserCheck size={20} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>
                    {selectedPlayer.first_name || selectedPlayer.last_name
                      ? `${selectedPlayer.first_name || ''} ${selectedPlayer.last_name || ''}`.trim()
                      : selectedPlayer.display_name}
                  </div>
                  {selectedPlayer.display_name && (selectedPlayer.first_name || selectedPlayer.last_name) && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {selectedPlayer.display_name}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleClearSelection}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* Manual entry form (or editable fields after selecting a user) */}
            {(showManual || selectedPlayer) && (
              <>
                {showManual && (
                  <button
                    type="button"
                    onClick={() => { setShowManual(false); setSearchText('') }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      padding: '0 0 0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    &larr; Back to search
                  </button>
                )}

                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Tiger"
                    autoFocus={showManual}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email {showManual ? '(optional)' : ''}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g. tiger@golf.com"
                    readOnly={!!selectedPlayer}
                    style={selectedPlayer ? { opacity: 0.6 } : undefined}
                  />
                </div>

                <div className="form-group">
                  <label>GHIN Number (optional)</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={ghinNumber}
                      onChange={(e) => setGhinNumber(e.target.value)}
                      placeholder="e.g. 1234567"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!ghinNumber.trim()) return
                        ghinLookup.mutate(ghinNumber.trim(), {
                          onSuccess: (data) => {
                            if (data.handicap_index != null) {
                              const split = splitHandicapForInput(data.handicap_index)
                              setHandicapIndex(split.magnitude)
                              setIsPlus(split.isPlus)
                            }
                            if (data.gender) setGender(data.gender)
                          },
                        })
                      }}
                      disabled={ghinLookup.isPending || !ghinNumber.trim()}
                      style={{
                        height: '44px',
                        padding: '0 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        color: '#fff',
                        backgroundColor: 'var(--accent-primary)',
                        border: 'none',
                        cursor: ghinLookup.isPending || !ghinNumber.trim() ? 'not-allowed' : 'pointer',
                        opacity: ghinLookup.isPending || !ghinNumber.trim() ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        flexShrink: 0,
                      }}
                    >
                      <RefreshCw size={14} style={ghinLookup.isPending ? { animation: 'spin 1s linear infinite' } : {}} />
                      Lookup
                    </button>
                  </div>
                  {ghinLookup.isError && (
                    <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                      {ghinLookup.error?.message || 'GHIN lookup failed'}
                    </div>
                  )}
                  {ghinLookup.isSuccess && (
                    <div style={{ color: 'var(--accent-primary)', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                      Handicap updated from GHIN
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Handicap Index (optional)</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <PlusHandicapToggle isPlus={isPlus} onChange={setIsPlus} />
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.1"
                      value={handicapIndex}
                      onChange={(e) => setHandicapIndex(e.target.value)}
                      placeholder="e.g. 12.4"
                      style={{ flex: 1 }}
                    />
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>
                    Tap <strong>+ Plus</strong> for handicaps better than scratch (e.g. +1.4)
                  </p>
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                    {[{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }].map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setGender(value)}
                        style={{
                          flex: 1, padding: '0.5rem', borderRadius: '0.5rem',
                          border: `1px solid ${gender === value ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                          backgroundColor: gender === value ? 'var(--accent-primary)' : 'var(--bg-primary)',
                          color: gender === value ? '#fff' : 'var(--text-primary)',
                          fontWeight: gender === value ? 600 : 400,
                          fontSize: '0.875rem', cursor: 'pointer',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Avatar Color</label>
                  <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                    marginTop: '0.25rem',
                  }}>
                    {AVATAR_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setColor(c)}
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          backgroundColor: c,
                          border: color === c ? '3px solid var(--text-primary)' : '3px solid transparent',
                          transition: 'border-color 0.15s',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {(showManual || selectedPlayer) && (
            <div className="modal-actions">
              <button type="button" className="btn-secondary btn-action" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary btn-action"
                disabled={!name.trim() || createPlayer.isPending}
                style={{ opacity: (!name.trim() || createPlayer.isPending) ? 0.5 : 1 }}
              >
                {createPlayer.isPending ? 'Adding...' : 'Add Player'}
              </button>
            </div>
          )}
        </form>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
